import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import type { DeepKeys } from '@tanstack/react-form'
import type { RegisterCustomMcpPayload } from 'v2/shared/services/aci-integrations-service'
import { z } from 'zod'

/**
 * A remote MCP server authenticates with exactly ONE outbound header, so each mode here is a
 * recipe for building it. `custom_header` covers everything with a bespoke name — X-API-Key,
 * X-Token, or a non-Bearer `Authorization` scheme — which is why the header name is free text.
 *
 * These rules mirror the bridge's (aci `bridge/mcp_client.py`). The bridge is the authority and
 * re-validates everything; duplicating the checks here only buys an inline error instead of a
 * round-trip 400.
 */

/** RFC 9110 field-name grammar. A whitelist: anything outside it is header injection. */
const HEADER_NAME_PATTERN = /^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/

/**
 * Header names the MCP transport owns — setting them could break the handshake or redirect the
 * request. `authorization` is deliberately absent: allowing `Authorization: Token <t>` is the
 * point of the custom-header mode.
 */
const RESERVED_HEADER_NAMES = new Set([
  'host',
  'content-type',
  'content-length',
  'accept',
  'accept-encoding',
  'connection',
  'transfer-encoding',
  'te',
  'upgrade',
  'expect',
  'mcp-session-id',
  'mcp-protocol-version',
])

/** Scheme comparison, not a string prefix: `HTTPS://host` is a valid HTTPS URL. */
const isHttpsUrl = (value: string) => {
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

type AuthMode = 'none' | 'bearer' | 'custom_header' | 'basic'

const customMcpFieldsSchema = z.object({
  // Required: a custom server has no catalog entry to borrow a name from.
  name: z
    .string()
    .trim()
    .min(1, 'Enter a name for this server')
    .max(60, 'Keep it under 60 characters'),
  description: z.string().trim().max(140, 'Keep it under 140 characters').optional(),
  url: z.string().trim().url('Enter a valid URL').refine(isHttpsUrl, 'Use an HTTPS URL'),
  authType: z.enum(['none', 'bearer', 'custom_header', 'basic']),
  token: z.string().optional(),
  headerName: z.string().optional(),
  username: z.string().optional(),
})

/**
 * The label the credential field carries in each mode — it is one field wearing three hats.
 * `bearer` is plain "Token" rather than "Bearer token": the selected option directly above
 * already says Bearer, and repeating it labels the same thing twice on one screen.
 */
const TOKEN_LABELS: Record<AuthMode, string> = {
  none: 'Token',
  bearer: 'Token',
  custom_header: 'Header value',
  basic: 'Password',
}

/**
 * The modes, with the copy that explains each one. Single source of truth: the radio options are
 * generated from this, so a mode can never appear in the picker without a description or drift
 * from the one the preview describes.
 */
const AUTH_MODES: { value: AuthMode; title: string; description: string }[] = [
  { value: 'none', title: 'No auth', description: 'The server is public, or it allow-lists us' },
  { value: 'bearer', title: 'Bearer token', description: 'Sent as Authorization: Bearer <token>' },
  {
    value: 'custom_header',
    title: 'Custom header',
    description: 'The server expects a header of its own, like X-API-Key',
  },
  {
    value: 'basic',
    title: 'Username & password',
    description: 'HTTP basic authentication',
  },
]

/**
 * Fixed-width mask. Deliberately NOT proportional to the credential: echoing its length back on
 * screen leaks information about the secret for no benefit.
 */
const MASK = '••••••••••'

/**
 * Switching mode swaps which inputs exist, which without this reads as a jump. Entry-only: the
 * form unmounts a hidden field outright, so there is no exit to animate.
 */
const REVEAL = 'animate-in fade-in slide-in-from-top-1 duration-200'

/**
 * REQUIRED on every field whose `visible` reads `authType`, not an optimisation.
 *
 * Each field renders inside its own `form.Field`, which subscribes only to ITS OWN state. A
 * `visible` predicate that depends on a DIFFERENT field is therefore never re-evaluated when
 * that field changes — the input silently never appears. Declaring the dependency is what
 * re-renders this field when the mode changes.
 *
 * This is why picking an auth mode revealed nothing before: the original `bearer` token field
 * had the predicate and not the dependency.
 */
const LISTEN_TO_AUTH_TYPE: DeepKeys<CustomMcpFormValues>[] = ['authType']

/**
 * What the bridge will actually put on the wire, for the preview under the form.
 *
 * This is the one thing the inputs alone cannot tell a user. `basic` composes a base64 blob they
 * can't eyeball, and `bearer` wraps their token in a header they never see — so a mistyped header
 * name or a password pasted into the username box is otherwise invisible until the connect fails.
 * Returns null while the mode's fields are still incomplete, so the preview appears only once it
 * would say something true.
 */
export const describeHeader = (
  values: CustomMcpFormValues,
): { name: string; value: string; note?: string } | null => {
  const token = values.token?.trim()
  if (!token) return null
  if (values.authType === 'bearer') return { name: 'Authorization', value: `Bearer ${MASK}` }
  if (values.authType === 'custom_header') {
    const name = values.headerName?.trim()
    return name ? { name, value: MASK } : null
  }
  if (values.authType === 'basic') {
    const username = values.username?.trim()
    return username
      ? {
          name: 'Authorization',
          value: `Basic ${MASK}`,
          note: `encodes ${username}:${MASK}`,
        }
      : null
  }
  return null
}

export const customMcpFormSchema = customMcpFieldsSchema.superRefine((values, context) => {
  const { authType } = values

  if (authType !== 'none' && !values.token?.trim()) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['token'],
      message: `Enter the ${TOKEN_LABELS[authType].toLowerCase()}, or choose No auth`,
    })
  }

  if (authType === 'custom_header') {
    const headerName = values.headerName?.trim() ?? ''
    if (!headerName) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['headerName'],
        message: 'Enter the header name, for example X-API-Key',
      })
    } else if (!HEADER_NAME_PATTERN.test(headerName)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['headerName'],
        // Describes what to REMOVE rather than listing the allowed set: the RFC 9110 token
        // charset also permits ! # $ % & ' * + ^ | ~, so an "only letters, digits and - _ . ~"
        // message sends someone to fix a header name that was never the problem.
        message: 'Header names cannot contain spaces, colons or other separator characters',
      })
    } else if (RESERVED_HEADER_NAMES.has(headerName.toLowerCase())) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['headerName'],
        message: `${headerName} is reserved by the MCP connection and cannot be set`,
      })
    }
  }

  if (authType === 'basic') {
    const username = values.username?.trim() ?? ''
    if (!username) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['username'],
        message: 'Enter the username',
      })
    } else if (username.includes(':')) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['username'],
        message: 'A username cannot contain a colon',
      })
    }
  }
})

export type CustomMcpFormValues = z.infer<typeof customMcpFormSchema>

/**
 * Form values -> request body, sending ONLY the fields the selected mode uses.
 *
 * The filtering is load-bearing, not tidiness: the fields stay mounted in form state after the
 * user switches modes, so a leftover `headerName` from an abandoned selection would otherwise
 * ride along on a `bearer` registration — and the bridge rejects auth fields belonging to a
 * mode other than the one chosen.
 */
export const toCustomMcpPayload = (
  values: CustomMcpFormValues,
  /** A PNG, WebP or JPEG data URI. */
  icon?: string | null,
): RegisterCustomMcpPayload => {
  const payload: RegisterCustomMcpPayload = {
    name: values.name.trim(),
    url: values.url.trim(),
    auth_type: values.authType,
  }
  const description = values.description?.trim()
  if (description) payload.description = description
  if (icon) payload.icon = icon
  if (values.authType === 'none') return payload

  // Whitespace is only safe to strip where the PROTOCOL says it is not part of the value.
  //
  //   bearer / custom_header -> the credential travels as an HTTP field value, and HTTP does not
  //     treat surrounding whitespace as content, so trimming a pasted token is a real kindness.
  //   basic -> ACI base64-encodes `username:password`, so every byte survives verbatim. Trimming
  //     " secret " to "secret" silently sends a DIFFERENT password and the server rejects every
  //     call, with nothing in the UI to suggest why. Blank-only input is still refused by the
  //     schema; that is validation, not mutation.
  payload.token = values.authType === 'basic' ? values.token : values.token?.trim()
  if (values.authType === 'custom_header') payload.header_name = values.headerName?.trim()
  if (values.authType === 'basic') payload.username = values.username?.trim()
  return payload
}

export const customMcpDefaultValues: CustomMcpFormValues = {
  name: '',
  description: '',
  url: '',
  authType: 'none',
  token: '',
  headerName: '',
  username: '',
}

export const customMcpFormFields: GenericFieldConfig<CustomMcpFormValues> = [
  {
    name: 'name',
    label: 'Name',
    componentType: 'input',
    zodValidation: customMcpFieldsSchema.shape.name,
    inputProps: {
      placeholder: 'e.g. Order Desk',
      'data-testid': 'input-mcp-name',
    },
  },
  {
    name: 'description',
    label: 'Description (optional)',
    componentType: 'input',
    zodValidation: customMcpFieldsSchema.shape.description,
    inputProps: {
      placeholder: 'Explain what it does in a few words',
      'data-testid': 'input-mcp-description',
    },
  },
  {
    name: 'url',
    label: 'Server URL',
    componentType: 'input',
    zodValidation: customMcpFieldsSchema.shape.url,
    inputProps: {
      inputMode: 'url',
      placeholder: 'https://mcp.example.com/mcp',
      'data-testid': 'input-mcp-url',
    },
  },
  {
    name: 'authType',
    label: 'Authentication',
    componentType: 'radioGroup',
    zodValidation: customMcpFieldsSchema.shape.authType,
    inputProps: {
      // Stacked, not the component's default `flex gap-12` row: four options laid out
      // horizontally overflow the 550px connect sheet. Stacking also buys room for the
      // one-line description that makes each mode self-explanatory.
      optionsClassName: 'flex flex-col gap-3 mt-2',
      labelClassName: 'cursor-pointer',
      options: AUTH_MODES.map(({ value, title, description }) => ({
        value,
        label: (
          <span className="flex flex-col gap-0.5">
            <span>{title}</span>
            <span className="text-xs-400 text-tertiary">{description}</span>
          </span>
        ),
      })),
      'data-testid': 'radio-mcp-auth-type',
    },
  },
  {
    name: 'headerName',
    label: 'Header name',
    componentType: 'input',
    visible: (values) => values.authType === 'custom_header',
    onChangeListenTo: LISTEN_TO_AUTH_TYPE,
    className: REVEAL,
    zodValidation: customMcpFieldsSchema.shape.headerName,
    inputProps: {
      placeholder: 'e.g. X-API-Key',
      'data-testid': 'input-mcp-header-name',
    },
  },
  {
    name: 'username',
    label: 'Username',
    componentType: 'input',
    visible: (values) => values.authType === 'basic',
    onChangeListenTo: LISTEN_TO_AUTH_TYPE,
    className: REVEAL,
    zodValidation: customMcpFieldsSchema.shape.username,
    inputProps: {
      placeholder: 'Username for the server',
      'data-testid': 'input-mcp-username',
    },
  },
  // One `token` value, three labels. `label` is static and hidden fields render null, so the
  // only way to name the credential correctly per mode is one entry per mode with mutually
  // exclusive `visible` predicates — exactly one is ever on screen.
  ...(['bearer', 'custom_header', 'basic'] as const).map((mode) => ({
    name: 'token' as const,
    label: TOKEN_LABELS[mode],
    componentType: 'inputPassword' as const,
    visible: (values: CustomMcpFormValues) => values.authType === mode,
    onChangeListenTo: LISTEN_TO_AUTH_TYPE,
    className: REVEAL,
    zodValidation: customMcpFieldsSchema.shape.token,
    inputProps: {
      placeholder: mode === 'basic' ? 'Password for the server' : 'Paste the value',
      'data-testid': 'input-mcp-token',
    },
  })),
]
